import { z } from 'zod';

/**
 * Seasonal themes (spec §6.12). A theme is an overlay: it changes how the office looks (tint,
 * particles) but never collision or object positions. The built-in themes are data; scheduling is
 * by calendar date in office time (Europe/Stockholm), with an admin override.
 */
export const THEME_KEYS = ['christmas', 'lucia', 'halloween', 'midsummer'] as const;
export type ThemeKey = (typeof THEME_KEYS)[number];

export type ThemeParticles = 'none' | 'snow' | 'leaves' | 'petals' | 'sparkles';

export interface MonthDay {
  readonly month: number; // 1-12
  readonly day: number;
}

export type ThemeSchedule =
  /** Every year from `from` to `to` inclusive (may wrap over New Year). */
  | { readonly kind: 'dates'; readonly from: MonthDay; readonly to: MonthDay }
  /** Midsummer Eve (the Friday between 19 and 25 June) to the following Sunday. */
  | { readonly kind: 'midsummer' };

export interface ThemeDefinition {
  readonly key: ThemeKey;
  readonly schedule: ThemeSchedule;
  /** A colour laid lightly over the whole view (0xRRGGBB). */
  readonly tint: number;
  readonly tintAlpha: number;
  readonly particles: ThemeParticles;
  /** When schedules overlap, the higher priority wins (Lucia beats Christmas on 13 December). */
  readonly priority: number;
}

export const THEMES: Readonly<Record<ThemeKey, ThemeDefinition>> = {
  christmas: {
    key: 'christmas',
    schedule: { kind: 'dates', from: { month: 12, day: 1 }, to: { month: 12, day: 26 } },
    tint: 0xff5a4f,
    tintAlpha: 0.05,
    particles: 'snow',
    priority: 1,
  },
  lucia: {
    key: 'lucia',
    schedule: { kind: 'dates', from: { month: 12, day: 13 }, to: { month: 12, day: 13 } },
    tint: 0xffd27a,
    tintAlpha: 0.08,
    particles: 'sparkles',
    priority: 2,
  },
  halloween: {
    key: 'halloween',
    schedule: { kind: 'dates', from: { month: 10, day: 24 }, to: { month: 11, day: 1 } },
    tint: 0x6b2fa0,
    tintAlpha: 0.08,
    particles: 'leaves',
    priority: 1,
  },
  midsummer: {
    key: 'midsummer',
    schedule: { kind: 'midsummer' },
    tint: 0xfff0a8,
    tintAlpha: 0.06,
    particles: 'petals',
    priority: 1,
  },
};

/** `auto` follows the schedule, `off` shows no theme, a key forces that theme. */
export const themeOverrideSchema = z.union([
  z.literal('auto'),
  z.literal('off'),
  z.enum(THEME_KEYS),
]);
export type ThemeOverride = z.infer<typeof themeOverrideSchema>;

export const updateThemeOverrideSchema = z.object({ override: themeOverrideSchema }).strict();
export type UpdateThemeOverrideRequest = z.infer<typeof updateThemeOverrideSchema>;

/** `GET /api/admin/themes`. */
export interface AdminThemesInfo {
  readonly override: ThemeOverride;
  /** The theme shown right now (`null` = none). */
  readonly active: ThemeKey | null;
  /** The theme the schedule alone would show today. */
  readonly scheduled: ThemeKey | null;
  readonly themes: readonly { readonly key: ThemeKey; readonly schedule: ThemeSchedule }[];
}

export function isThemeKey(value: unknown): value is ThemeKey {
  return typeof value === 'string' && (THEME_KEYS as readonly string[]).includes(value);
}

/** Calendar date of `date` in `timeZone`. */
export function officeDate(
  date: Date,
  timeZone: string,
): { year: number; month: number; day: number } {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)?.value);
  return { year: part('year'), month: part('month'), day: part('day') };
}

/** Day of June that is Midsummer Eve: the Friday between 19 and 25 June. */
export function midsummerEve(year: number): number {
  for (let day = 19; day <= 25; day += 1) {
    if (new Date(Date.UTC(year, 5, day)).getUTCDay() === 5) return day;
  }
  return 19; // unreachable: every 7-day span has a Friday
}

const ordinal = (month: number, day: number) => month * 100 + day;

function inSchedule(
  schedule: ThemeSchedule,
  today: { year: number; month: number; day: number },
): boolean {
  const now = ordinal(today.month, today.day);
  if (schedule.kind === 'midsummer') {
    const eve = midsummerEve(today.year);
    return today.month === 6 && today.day >= eve && today.day <= eve + 2;
  }
  const from = ordinal(schedule.from.month, schedule.from.day);
  const to = ordinal(schedule.to.month, schedule.to.day);
  return from <= to ? now >= from && now <= to : now >= from || now <= to;
}

/** The theme the schedule picks for `date` in office time, or `null`. */
export function scheduledTheme(date: Date, timeZone: string): ThemeKey | null {
  const today = officeDate(date, timeZone);
  let best: ThemeDefinition | undefined;
  for (const key of THEME_KEYS) {
    const theme = THEMES[key];
    if (inSchedule(theme.schedule, today) && (!best || theme.priority > best.priority)) {
      best = theme;
    }
  }
  return best?.key ?? null;
}

/** The theme to show, given the admin override. */
export function activeTheme(
  date: Date,
  timeZone: string,
  override: ThemeOverride,
): ThemeKey | null {
  if (override === 'off') return null;
  if (override !== 'auto') return override;
  return scheduledTheme(date, timeZone);
}
